import { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

type Props = {
  children: ReactNode;
  onClose?: () => void;
};

const isOverlay = (target: EventTarget | null) =>
  target instanceof HTMLElement && target.classList.contains('modal-overlay');

/** Рендер поверх страницы: Escape закрывает, если передан onClose. */
const ModalPortal = ({ children, onClose }: Props) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!onClose) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    // Выделение/копирование в поле: mousedown внутри, mouseup на подложке.
    // Без этого overlay onClick закрывает окно без сохранения.
    const downOnOverlay = { current: false };
    const onMouseDown = (event: MouseEvent) => {
      downOnOverlay.current = isOverlay(event.target);
    };
    const onClickCapture = (event: MouseEvent) => {
      if (isOverlay(event.target) && !downOnOverlay.current) {
        event.stopPropagation();
      }
      downOnOverlay.current = false;
    };
    document.addEventListener('mousedown', onMouseDown, true);
    document.addEventListener('click', onClickCapture, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown, true);
      document.removeEventListener('click', onClickCapture, true);
    };
  }, []);

  return createPortal(children, document.body);
};

export default ModalPortal;
